// src/pages/HechoDetalle.jsx
//
// El expediente. Aquí es donde la dirección elegida se nota: la línea de tiempo
// no es un widget dentro de la pantalla, ES la pantalla. El hecho, el folio, la
// asignación, cada documento, cada mensaje, la propuesta de la IA, la bitácora
// y los pendientes son entradas del mismo hilo, en orden, filtrables.
//
// Las tres capas que definió el equipo se distinguen visualmente y se filtran
// en el servidor: si el perfil no puede ver la bitácora, esas entradas nunca
// llegan al navegador.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    getIncident, addEntry, resolvePending, uploadAttachments, getAttachmentUrl,
    getPdf, structureNarrative, validateNarrative, advanceStage, getSimilares,
    listLawyers, assignLawyer, addCoLawyer, removeCoLawyer,
    closeIncident, deleteIncident, RESULTADOS_CIERRE, ETAPAS, etapaIndex, etapaLabel,
} from '../services/incidentApi';
import {
    HechosStyles, Membrete, Tracker, I, Pill, Campo, KIND,
    fmtFecha, fmtHora, fmtMonto, diasDesde,
} from '../components/hechos/ui';

// "Documentos" filtra por contenido, no solo por tipo de entrada: el PDF
// oficial emitido (kind 'sistema') y cualquier mensaje con archivo también son
// documentación del expediente. Sin esto, un expediente sin evidencia subida
// mostraba la pestaña vacía aunque el documento oficial ya existiera.
const FILTROS = [
    { key: 'todo',      label: 'Todo',        match: null },
    { key: 'documento', label: 'Documentos',  match: (e) => e.kind === 'documento' || (e.files?.length > 0) },
    { key: 'mensaje',   label: 'Mensajes',    match: (e) => e.kind === 'mensaje' },
    { key: 'pendiente', label: 'Pendientes',  match: (e) => e.kind === 'pendiente' },
    { key: 'legal',     label: 'Solo legal',  match: (e) => e.kind === 'bitacora' },
];

export default function HechoDetalle() {
    const { id } = useParams();
    const navigate = useNavigate();
    const fileRef = useRef(null);
    // Segundo input de archivos, para subir el documento EXACTO de un
    // pendiente: llega con su docType y el pendiente se cierra solo.
    const fileDocRef = useRef(null);
    const [docKeyPendiente, setDocKeyPendiente] = useState(null);

    const [data, setData]   = useState(null);
    const [cargando, setCargando] = useState(true);
    const [filtro, setFiltro] = useState('todo');
    const [texto, setTexto]   = useState('');
    const [modoLegal, setModoLegal] = useState(false);
    const [enviando, setEnviando]   = useState(false);
    const [procesandoIa, setProcesandoIa] = useState(false);
    const [similares, setSimilares] = useState(null);
    const [validando, setValidando] = useState(false);
    const [textoOficial, setTextoOficial] = useState('');
    const [reasignando, setReasignando] = useState(false);
    const [abogados, setAbogados] = useState(null);     // catálogo, se pide al abrir
    const [abogadoSel, setAbogadoSel] = useState('');
    const [guardandoAsignacion, setGuardandoAsignacion] = useState(false);
    const [equipoAbierto, setEquipoAbierto] = useState(false);
    const [coSel, setCoSel] = useState('');
    // Cierre formal y eliminación (definitiva, solo admin)
    const [cerrando, setCerrando] = useState(false);
    const [resultadoSel, setResultadoSel] = useState('');
    const [notaCierre, setNotaCierre] = useState('');
    const [borrando, setBorrando] = useState(false);
    const [folioConfirma, setFolioConfirma] = useState('');
    const [procesandoCierre, setProcesandoCierre] = useState(false);

    const cargar = useCallback(async () => {
        try {
            setData(await getIncident(id));
        } catch (e) {
            if (e.response?.status === 403) toast.error('Sin acceso a este expediente');
            else if (e.response?.status === 404) toast.error('Expediente no encontrado');
            else toast.error('No se pudo cargar el expediente');
            navigate('/hechos');
        } finally {
            setCargando(false);
        }
    }, [id, navigate]);

    useEffect(() => { cargar(); }, [cargar]);

    // Casos parecidos. Se pide aparte del expediente porque es información
    // complementaria: si el motor de coincidencias falla o tarda, el expediente
    // tiene que abrir igual.
    useEffect(() => {
        let vivo = true;
        getSimilares(id).then(r => vivo && setSimilares(r)).catch(() => {});
        return () => { vivo = false; };
    }, [id]);

    const inc = data?.incident;
    // Referencia estable: sin esto, el `|| []` crea un arreglo nuevo en cada
    // render y todos los useMemo de abajo se recalculan siempre.
    const entries = useMemo(() => data?.entries || [], [data]);
    const progreso = data?.progreso;
    // El servidor solo manda entradas 'legal' a quien puede verlas, así que su
    // mera presencia basta para saber si este perfil es del equipo legal.
    const puedeLegal = useMemo(() => entries.some(e => e.visibility === 'legal'), [entries]);
    // Reasignar abogado: lo decide el servidor (admin o coordinador legal).
    const puedeReasignar = !!data?.permisos?.reasignar;

    const pendientesAbiertos = useMemo(
        () => entries.filter(e => e.kind === 'pendiente' && !e.pending?.resolvedAt),
        [entries]
    );

    const visibles = useMemo(() => {
        const f = FILTROS.find(x => x.key === filtro);
        if (!f?.match) return entries;
        return entries.filter(f.match);
    }, [entries, filtro]);

    // Agrupación por día: en un expediente legal la fecha es información, no
    // adorno — separa "lo que pasó el día del hecho" de "lo que pasó después".
    const porDia = useMemo(() => {
        const grupos = [];
        for (const e of visibles) {
            const dia = new Date(e.occurredAt || e.createdAt).toDateString();
            const ult = grupos[grupos.length - 1];
            if (ult && ult.dia === dia) ult.items.push(e);
            else grupos.push({ dia, items: [e] });
        }
        return grupos;
    }, [visibles]);

    async function publicar() {
        if (!texto.trim()) return;
        setEnviando(true);
        try {
            await addEntry(id, texto.trim(), modoLegal ? 'legal' : 'publica');
            setTexto('');
            await cargar();
            toast.success(modoLegal ? 'Nota guardada en la bitácora' : 'Constancia agregada al expediente');
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo publicar');
        } finally {
            setEnviando(false);
        }
    }

    async function subir(archivos, docType) {
        if (!archivos?.length) return;
        try {
            const r = await uploadAttachments(id, archivos, docType);
            await cargar();
            toast.success(`Evidencia cargada — ${r.progreso.cargados} de ${r.progreso.requeridos} documentos`);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo subir la evidencia');
        }
    }

    async function abrirArchivo(idx) {
        try {
            const { url } = await getAttachmentUrl(id, idx);
            window.open(url, '_blank', 'noopener');
        } catch { toast.error('No se pudo abrir el archivo'); }
    }

    async function abrirPdf() {
        try {
            const { url } = await getPdf(id);
            window.open(url, '_blank', 'noopener');
        } catch (e) {
            toast.error(e.response?.status === 404
                ? 'El documento todavía se está generando'
                : 'No se pudo abrir el documento');
        }
    }

    async function procesarIa() {
        setProcesandoIa(true);
        try {
            await structureNarrative(id);
            await cargar();
            toast.success('Relato estructurado. Requiere validación del abogado.');
        } catch (e) {
            toast.error(e.response?.status === 503
                ? 'El asistente no está disponible ahora; puedes reintentar'
                : (e.response?.data?.error || 'No se pudo procesar el relato'));
        } finally {
            setProcesandoIa(false);
        }
    }

    async function cargarAbogados() {
        if (abogados !== null) return true;
        try {
            setAbogados((await listLawyers()).abogados);
            return true;
        } catch {
            toast.error('No se pudo cargar la lista de abogados');
            return false;
        }
    }

    async function abrirReasignacion() {
        setAbogadoSel('');
        setReasignando(true);
        if (!(await cargarAbogados())) setReasignando(false);
    }

    async function abrirEquipo() {
        setCoSel('');
        setEquipoAbierto(true);
        if (!(await cargarAbogados())) setEquipoAbierto(false);
    }

    async function sumarCoAbogado() {
        setGuardandoAsignacion(true);
        try {
            const r = await addCoLawyer(id, coSel);
            setEquipoAbierto(false);
            await cargar();
            toast.success(`${r.coLawyer.name} se sumó al equipo del caso`);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo sumar al abogado');
        } finally {
            setGuardandoAsignacion(false);
        }
    }

    async function quitarCoAbogado(c) {
        try {
            await removeCoLawyer(id, c._id);
            await cargar();
            toast.success(`${c.name} fue retirado del equipo del caso`);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo retirar al abogado');
        }
    }

    async function confirmarReasignacion() {
        setGuardandoAsignacion(true);
        try {
            const r = await assignLawyer(id, abogadoSel);
            setReasignando(false);
            await cargar();
            toast.success(`Expediente asignado a ${r.assignedLawyer.name}`);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo reasignar el expediente');
        } finally {
            setGuardandoAsignacion(false);
        }
    }

    async function confirmarCierre() {
        setProcesandoCierre(true);
        try {
            await closeIncident(id, { resultado: resultadoSel, nota: notaCierre.trim() });
            setCerrando(false);
            await cargar();
            toast.success('Expediente cerrado');
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo cerrar el expediente');
        } finally {
            setProcesandoCierre(false);
        }
    }

    async function confirmarBorrado() {
        setProcesandoCierre(true);
        try {
            const r = await deleteIncident(id);
            toast.success(`Expediente ${r.folio} eliminado definitivamente`);
            navigate('/hechos');
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo eliminar el expediente');
            setProcesandoCierre(false);
        }
    }

    function abrirValidacion() {
        setTextoOficial(inc?.narrativeFinal?.texto || inc?.ai?.resumen || '');
        setValidando(true);
    }

    async function validar() {
        try {
            await validateNarrative(id, { texto: textoOficial.trim(), eventType: inc.eventType?._id });
            setValidando(false);
            await cargar();
            toast.success('Relato validado. El documento se está reemitiendo.');
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo validar');
        }
    }

    async function avanzar() {
        const siguiente = ETAPAS[etapaIndex(inc.etapa) + 1];
        if (!siguiente) return;
        try {
            await advanceStage(id, siguiente.key);
            await cargar();
            toast.success(`Expediente en "${siguiente.label}"`);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo avanzar la etapa');
        }
    }

    function pedirDocumento(docKey) {
        setDocKeyPendiente(docKey);
        fileDocRef.current?.click();
    }

    async function cerrarPendiente(entryId) {
        try {
            await resolvePending(id, entryId);
            await cargar();
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo cerrar el pendiente');
        }
    }

    if (cargando) return <div className="ho-wrap"><HechosStyles /><div className="ho-empty">Cargando expediente…</div></div>;
    if (!inc) return null;

    const col = inc.colaboradores?.[0] || {};

    return (
        <div className="ho-wrap ho-narrow">
            <HechosStyles />
            <Membrete />

            <button className="btn btn-link px-0 d-inline-flex align-items-center gap-2 mb-2 mt-3"
                    style={{ color: 'var(--c-text-3)', textDecoration: 'none', fontSize: 13 }}
                    onClick={() => navigate('/hechos')}>
                {I.back} Registro
            </button>

            {/* ── Encabezado ─────────────────────────────────────────── */}
            <header className="ho-head" style={{ paddingTop: 14 }}>
                <div className="d-flex align-items-start gap-3 flex-wrap">
                    <div style={{ flex: 1, minWidth: 240 }}>
                        <div className="ho-kicker">Expediente</div>
                        <h1 className="ho-h1">
                            {inc.eventType?.name || 'Hecho operativo sin clasificar'}
                            {col.nombre ? ` a ${col.nombre}` : ''}
                            {inc.municipio ? `, ${inc.municipio}` : ''}
                        </h1>
                    </div>
                    <div className="d-flex flex-column align-items-end gap-2">
                        <div className="ho-sello">
                            <div className="k">Folio</div>
                            <div className="v">{inc.folio}</div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap justify-content-end">
                            {puedeReasignar && (
                                <button className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                                        onClick={abrirReasignacion}>
                                    {I.user} {inc.assignedLawyer ? 'Reasignar abogado' : 'Asignar abogado'}
                                </button>
                            )}
                            <button className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                                    onClick={abrirPdf}>{I.pdf} PDF</button>
                            {/* -2: la última etapa "avanzable" es gestión legal; a
                                concluido se llega cerrando el expediente, no avanzando. */}
                            {puedeLegal && etapaIndex(inc.etapa) < ETAPAS.length - 2 && (
                                <button className="btn btn-primary btn-sm" onClick={avanzar}>Avanzar etapa</button>
                            )}
                            {data?.permisos?.cerrar && (
                                <button className="btn btn-outline-secondary btn-sm"
                                        onClick={() => { setResultadoSel(''); setNotaCierre(''); setCerrando(true); }}>
                                    Cerrar expediente
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <div className="ho-head-sub">
                    {inc.status === 'cerrado' && (
                        <span>
                            <Pill tone="ok" icon={I.check}>
                                Cerrado{inc.resultado ? ` — ${RESULTADOS_CIERRE.find(r => r.key === inc.resultado)?.label || inc.resultado}` : ''}
                            </Pill>
                        </span>
                    )}
                    {inc.marca && <span>Marca <b>{inc.marca}</b></span>}
                    {inc.reportante?.sucursal && <span>Sucursal <b>{inc.reportante.sucursal}</b></span>}
                    {inc.estado && <span>Zona legal <b>{inc.estado}</b></span>}
                    <span>Abogado <b>{inc.assignedLawyer?.name || 'sin asignar'}</b></span>
                    <span>
                        Equipo{' '}
                        {(inc.coLawyers || []).length ? (
                            (inc.coLawyers || []).map(c => (
                                <b key={c._id} style={{ marginRight: 6 }}>
                                    {c.name}
                                    {puedeReasignar && (
                                        <button className="btn btn-link p-0 ms-1"
                                                style={{ color: 'var(--c-text-3)', lineHeight: 1, fontSize: 12, textDecoration: 'none' }}
                                                title="Retirar del caso" onClick={() => quitarCoAbogado(c)}>×</button>
                                    )}
                                </b>
                            ))
                        ) : <b>—</b>}
                        {puedeReasignar && (
                            <button className="ho-chip" style={{ marginLeft: 8, padding: '1px 8px', fontSize: 11 }}
                                    onClick={abrirEquipo}>+ Sumar abogado</button>
                        )}
                    </span>
                </div>
            </header>

            {/* ── Indicadores ────────────────────────────────────────── */}
            <div className="ho-strip">
                <div><div className="k">Detrimento</div><div className="v">{fmtMonto(inc.montoDetrimento)}</div></div>
                <div><div className="k">Ocurrió</div><div className="v">{fmtFecha(inc.fechaDelHecho, false).replace(/ de \d{4}/, '')}<small> {fmtHora(inc.fechaDelHecho)}</small></div></div>
                <div><div className="k">Abierto hace</div><div className="v">{diasDesde(inc.createdAt)}<small> días</small></div></div>
                {progreso?.requeridos > 0 && (
                    <div><div className="k">Documentos</div><div className="v">{progreso.cargados}<small> de {progreso.requeridos}</small></div></div>
                )}
            </div>

            <Tracker etapas={ETAPAS} actual={inc.etapa} />

            {/* Lo que detiene el expediente: el dato más accionable de la pantalla */}
            {pendientesAbiertos.length > 0 && (
                <div className="ho-focus">
                    <span style={{ color: 'var(--ho-pend)', marginTop: 1 }}>{I.alert}</span>
                    <div>
                        <div className="t">
                            {pendientesAbiertos.length === 1
                                ? '1 requisito pendiente para avanzar de etapa'
                                : `${pendientesAbiertos.length} requisitos pendientes para avanzar de etapa`}
                        </div>
                        <div className="d">
                            {pendientesAbiertos.slice(0, 3).map(p => p.title).join(' · ')}
                            {pendientesAbiertos.length > 3 && ` y ${pendientesAbiertos.length - 3} más`}
                            {' — Responsable: '}
                            <strong>{pendientesAbiertos[0].pending?.owner === 'legal' ? 'Equipo legal'
                                   : pendientesAbiertos[0].pending?.owner === 'rh' ? 'Recursos Humanos'
                                   : 'Gerencia de sucursal'}</strong>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Datos del hecho ────────────────────────────────────── */}
            <div className="ho-fs">
                <div className="ho-fs-title" style={{ marginBottom: 14 }}>Datos del expediente</div>
                <div className="ho-kv">
                    <Campo label="Colaborador">{col.nombre}{col.idEmpleado ? <> · <span className="ho-mono">{col.idEmpleado}</span></> : null}</Campo>
                    <Campo label="Cargo">{col.cargo}</Campo>
                    <Campo label="Ruta">{col.rutaCobranza}</Campo>
                    <Campo label="Lugar de los hechos">{inc.lugarDeLosHechos || [inc.municipio, inc.estado].filter(Boolean).join(', ')}</Campo>
                    <Campo label="Reportó">{inc.reportante?.nombre}</Campo>
                    <Campo label="Vehículo con la autoridad">
                        {inc.vehiculoEnPoderAutoridad?.aplica ? (inc.vehiculoEnPoderAutoridad.detalle || 'Sí') : 'No'}
                    </Campo>
                    {inc.articulosPersonales && <Campo label="Artículos personales">{inc.articulosPersonales}</Campo>}
                    {/* Solo equipo legal: a quién se notificó el reporte */}
                    {data?.permisos?.legal && inc.destinatarios?.length > 0 && (
                        <div className="ho-campo" style={{ gridColumn: '1 / -1' }}>
                            <div className="ho-campo-k">Destinatarios del reporte</div>
                            <div className="d-flex flex-wrap gap-2" style={{ marginTop: 6 }}>
                                {inc.destinatarios.map(d => (
                                    <Pill key={d.email} tone={d.origen === 'protocolo' ? 'info' : 'neutro'} icon={I.mail}>
                                        {d.email}
                                    </Pill>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Relato ─────────────────────────────────────────────── */}
            <div className="ho-fs">
                <div className="d-flex align-items-center gap-3 flex-wrap" style={{ marginBottom: 12 }}>
                    <div className="ho-fs-title">Relato</div>
                    {inc.narrativeFinal?.texto
                        ? <Pill tone="ok" icon={I.check}>Validado{inc.narrativeFinal.edited ? ' con correcciones' : ''}</Pill>
                        : <Pill tone="pend" icon={I.alert}>Pendiente de validación</Pill>}
                    <div className="ms-auto d-flex gap-2">
                        <button className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                                onClick={procesarIa} disabled={procesandoIa}>
                            {I.pen} {procesandoIa ? 'Estructurando el relato…' : inc.ai?.processedAt ? 'Estructurar de nuevo' : 'Estructurar relato'}
                        </button>
                        {puedeLegal && (
                            <button className="btn btn-primary btn-sm" onClick={abrirValidacion}>
                                {inc.narrativeFinal?.texto ? 'Corregir versión oficial' : 'Validar relato'}
                            </button>
                        )}
                    </div>
                </div>

                {inc.narrativeFinal?.texto ? (
                    <>
                        <div className="ho-kicker" style={{ marginBottom: 6 }}>Versión oficial — la que se imprime</div>
                        <p style={{ fontSize: 14.5, lineHeight: 1.75, color: 'var(--c-text-1)' }}>{inc.narrativeFinal.texto}</p>
                    </>
                ) : inc.ai?.resumen ? (
                    // Resumen propuesto por el asistente, listo apenas se levanta el
                    // hecho: el abogado lo lee (junto al original) y luego valida.
                    <div style={{
                        padding: '14px 16px', borderRadius: 6,
                        background: 'color-mix(in srgb, var(--ho-accent) 7%, var(--c-card))',
                        border: '1px solid color-mix(in srgb, var(--ho-accent) 26%, transparent)',
                    }}>
                        <div className="d-flex align-items-center gap-2" style={{ marginBottom: 8 }}>
                            <span style={{ color: 'var(--ho-accent)' }}>{I.pen}</span>
                            <div className="ho-kicker" style={{ color: 'var(--ho-accent)' }}>Resumen propuesto por el asistente</div>
                        </div>
                        <p style={{ fontSize: 14.5, lineHeight: 1.75, color: 'var(--c-text-1)', margin: 0 }}>{inc.ai.resumen}</p>
                        <p style={{ fontSize: 12, color: 'var(--c-text-3)', margin: '10px 0 0' }}>
                            Pendiente de validación. El abogado lo confirma o corrige antes de que sea oficial; el original nunca se sustituye.
                        </p>
                    </div>
                ) : inc.ai?.error ? (
                    <p style={{ fontSize: 12.5, color: 'var(--c-text-3)' }}>
                        El asistente no pudo generar el resumen automático. Puedes solicitarlo con “Estructurar relato”.
                    </p>
                ) : (
                    <p style={{ fontSize: 12.5, color: 'var(--c-text-3)' }}>
                        El asistente está preparando el resumen propuesto…
                    </p>
                )}

                <div className="ho-original">
                    <div className="ho-kicker" style={{ marginBottom: 8 }}>Relato original</div>
                    <div className="txt">{inc.narrativeRaw}</div>
                </div>

                {/* Propuesta de la IA: siempre etiquetada como propuesta */}
                {inc.ai?.processedAt && !inc.ai?.error && (
                    <div style={{ marginTop: 20 }}>
                        {inc.ai.cronologia?.length > 0 && (
                            <div className="ho-kicker" style={{ color: 'var(--ho-accent)', marginBottom: 10 }}>
                                Propuesta de orden cronológico
                            </div>
                        )}
                        {inc.ai.cronologia?.length > 0 && inc.ai.cronologia.map((c, i) => (
                            <div className="ho-ev" key={i}>
                                <div className="t">{c.hora}</div>
                                <div className="sp"><span className="node" style={{ color: 'var(--ho-accent)', width: 13, height: 13, marginTop: 7 }} /></div>
                                <div className="bd" style={{ paddingBottom: 14 }}>
                                    <div className="dsc" style={{ marginTop: 0 }}>{c.texto}</div>
                                </div>
                            </div>
                        ))}
                        {inc.ai.datosClave?.length > 0 && (
                            <div className="d-flex flex-wrap gap-2" style={{ marginTop: 12 }}>
                                {inc.ai.datosClave.map((d, i) => (
                                    <Pill key={i} tone="neutro">
                                        <span style={{ color: 'var(--c-text-3)', fontWeight: 400 }}>{d.etiqueta}</span> {d.valor}
                                    </Pill>
                                ))}
                            </div>
                        )}
                        {inc.ai.inconsistencias && (
                            <div className="ho-focus" style={{ marginTop: 14 }}>
                                <span style={{ color: 'var(--ho-pend)', marginTop: 1 }}>{I.alert}</span>
                                <div><div className="t">Puntos por aclarar en el relato</div>
                                     <div className="d">{inc.ai.inconsistencias}</div></div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Casos parecidos ────────────────────────────────────── */}
            {similares?.items?.length > 0 && (
                <div className="ho-fs">
                    <div className="d-flex align-items-center gap-3 flex-wrap" style={{ marginBottom: 4 }}>
                        <div className="ho-fs-title">Casos parecidos</div>
                        <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>{similares.advertencia}</span>
                    </div>
                    {similares.items.map(s => (
                        <div key={s.folio} className="ho-pend-box"
                             style={{ marginTop: 10, background: 'var(--c-card)', borderColor: 'var(--c-border)' }}>
                            <div style={{ flex: 1, minWidth: 220 }}>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    {s.visible ? (
                                        <button className="btn btn-link p-0 ho-mono"
                                                style={{ fontSize: 13, textDecoration: 'none' }}
                                                onClick={() => navigate(`/hechos/${s._id}`)}>{s.folio}</button>
                                    ) : (
                                        <span className="ho-mono" style={{ fontSize: 13, color: 'var(--c-text-3)' }}>{s.folio}</span>
                                    )}
                                    <span style={{ fontSize: 13.5, fontWeight: 600 }}>{s.tipo}</span>
                                    <span style={{ fontSize: 12.5, color: 'var(--c-text-3)' }}>
                                        {s.lugar} · {fmtFecha(s.fecha, false)}
                                    </span>
                                    {!s.visible && <Pill tone="neutro" icon={I.lock}>Fuera de tu ámbito</Pill>}
                                </div>
                                <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12.5, color: 'var(--c-text-2)' }}>
                                    {s.razones.map((r, i) => <li key={i}>{r}</li>)}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Cronología ─────────────────────────────────────────── */}
            <div className="ho-fs-title" style={{ marginTop: 30 }}>Cronología</div>
            <div className="ho-filters" role="group" aria-label="Filtrar el expediente" style={{ paddingTop: 10 }}>
                {FILTROS.filter(f => f.key !== 'legal' || puedeLegal).map(f => (
                    <button key={f.key} className="ho-chip" aria-pressed={filtro === f.key}
                            onClick={() => setFiltro(f.key)}>{f.label}</button>
                ))}
                <span className="ms-auto" />
                {inc.status !== 'cerrado' && (
                    <button className="ho-chip d-inline-flex align-items-center gap-2"
                            onClick={() => fileRef.current?.click()}>{I.clip} Subir evidencia</button>
                )}
                <input ref={fileRef} type="file" multiple hidden
                       onChange={e => { subir(e.target.files); e.target.value = ''; }} />
                <input ref={fileDocRef} type="file" multiple hidden
                       onChange={e => { subir(e.target.files, docKeyPendiente); e.target.value = ''; }} />
            </div>

            {porDia.length === 0 ? (
                <div className="ho-empty">No hay entradas de este tipo en el expediente.</div>
            ) : porDia.map(({ dia, items }) => (
                <React.Fragment key={dia}>
                    <div className="ho-day">
                        {new Date(dia).toDateString() === new Date().toDateString()
                            ? 'Hoy'
                            : new Date(dia).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                    {items.map(e => (
                        <Entrada key={e._id} e={e} onArchivo={abrirArchivo}
                                 attachments={inc.attachments} onResolver={cerrarPendiente}
                                 onSubirDoc={pedirDocumento} />
                    ))}
                </React.Fragment>
            ))}

            {/* ── Escribir en el expediente ──────────────────────────── */}
            {inc.status === 'cerrado' ? (
                <div className="ho-empty" style={{ padding: '28px 0 6px' }}>
                    Expediente cerrado — la cronología queda como constancia.
                </div>
            ) : (
            <div className={`ho-composer ${modoLegal ? 'legal' : ''}`}>
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                    <div className="ho-kicker">{modoLegal ? 'Nota de bitácora' : 'Escribir en el expediente'}</div>
                    {modoLegal
                        ? <Pill tone="legal" icon={I.lock}>Solo abogados · no se notifica a nadie más</Pill>
                        : <Pill tone="info">Visible para el reportante, sus superiores, RH y legal</Pill>}
                    {puedeLegal && (
                        <button className="ho-chip ms-auto" aria-pressed={modoLegal}
                                onClick={() => setModoLegal(v => !v)}>
                            {modoLegal ? 'Cambiar al hilo público' : 'Cambiar a bitácora legal'}
                        </button>
                    )}
                </div>
                <textarea value={texto} onChange={e => setTexto(e.target.value)}
                          placeholder={modoLegal
                              ? 'Valoración, estrategia, riesgos. No sale en el PDF ni se notifica.'
                              : 'Deja constancia de un avance, solicita información o responde. Queda en el historial del expediente.'} />
                <div className="d-flex justify-content-end mt-2">
                    <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={publicar} disabled={enviando || !texto.trim()}>
                        {I.send} {enviando ? 'Publicando…' : modoLegal ? 'Guardar nota' : 'Publicar'}
                    </button>
                </div>
            </div>
            )}

            {/* Zona de peligro: solo admin, discreta y al fondo */}
            {data?.permisos?.borrar && (
                <div className="d-flex justify-content-end" style={{ marginTop: 14 }}>
                    <button className="btn btn-link btn-sm p-0"
                            style={{ color: 'var(--ho-hecho)', textDecoration: 'none', fontSize: 12 }}
                            onClick={() => { setFolioConfirma(''); setBorrando(true); }}>
                        Eliminar expediente…
                    </button>
                </div>
            )}

            {/* ── Cierre formal del expediente ───────────────────────── */}
            {cerrando && (
                <div className="ho-modal-bk" onClick={e => e.target === e.currentTarget && setCerrando(false)}>
                    <div className="ho-modal" role="dialog" aria-modal="true" aria-label="Cerrar expediente">
                        <div className="ho-kicker">Cierre</div>
                        <h2 className="ho-h2" style={{ marginTop: 6 }}>Cerrar el expediente</h2>
                        <label className="ho-lbl" style={{ marginTop: 16 }}>¿Cómo terminó el caso?</label>
                        <select className="form-select" value={resultadoSel}
                                onChange={e => setResultadoSel(e.target.value)}>
                            <option value="">— elige el resultado —</option>
                            {RESULTADOS_CIERRE.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                        </select>
                        <label className="ho-lbl" style={{ marginTop: 16 }}>Nota de cierre (opcional)</label>
                        <textarea style={{ minHeight: 90, marginTop: 4 }} value={notaCierre}
                                  onChange={e => setNotaCierre(e.target.value)}
                                  placeholder="Monto recuperado, número de carpeta, acuerdo alcanzado…" />
                        <p className="aviso">
                            El expediente pasa a "Concluido", deja de aceptar publicaciones y evidencia, y el
                            resultado alimenta el reporte de gestión. Queda constancia en la cronología.
                        </p>
                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setCerrando(false)}>Cancelar</button>
                            <button className="btn btn-primary btn-sm" onClick={confirmarCierre}
                                    disabled={!resultadoSel || procesandoCierre}>
                                {procesandoCierre ? 'Cerrando…' : 'Cerrar expediente'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Eliminación definitiva (solo admin) ────────────────── */}
            {borrando && (
                <div className="ho-modal-bk" onClick={e => e.target === e.currentTarget && setBorrando(false)}>
                    <div className="ho-modal" role="dialog" aria-modal="true" aria-label="Eliminar expediente"
                         style={{ borderTopColor: 'var(--ho-hecho)' }}>
                        <div className="ho-kicker" style={{ color: 'var(--ho-hecho)' }}>Eliminación definitiva</div>
                        <h2 className="ho-h2" style={{ marginTop: 6 }}>Eliminar el expediente</h2>
                        <p style={{ fontSize: 13, color: 'var(--c-text-2)', lineHeight: 1.6, margin: '10px 0 0' }}>
                            Se borra el expediente <b className="ho-mono">{inc.folio}</b> y toda su cronología.
                            No hay forma de recuperarlo. Es para registros de prueba o levantados por
                            error — si el caso terminó, ciérralo en su lugar.
                        </p>
                        <label className="ho-lbl" style={{ marginTop: 16 }}>Escribe el folio para confirmar</label>
                        <input className="form-control ho-mono" value={folioConfirma}
                               onChange={e => setFolioConfirma(e.target.value)} placeholder={inc.folio} />
                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setBorrando(false)}>Cancelar</button>
                            <button className="btn btn-danger btn-sm" onClick={confirmarBorrado}
                                    disabled={folioConfirma.trim() !== inc.folio || procesandoCierre}>
                                {procesandoCierre ? 'Eliminando…' : 'Eliminar definitivamente'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Reasignación de abogado ────────────────────────────── */}
            {reasignando && (
                <div className="ho-modal-bk" onClick={e => e.target === e.currentTarget && setReasignando(false)}>
                    <div className="ho-modal" role="dialog" aria-modal="true" aria-label="Reasignar abogado">
                        <div className="ho-kicker">Asignación</div>
                        <h2 className="ho-h2" style={{ marginTop: 6 }}>
                            {inc.assignedLawyer ? 'Reasignar el expediente' : 'Asignar el expediente'}
                        </h2>
                        {abogados === null ? (
                            <div className="ho-empty" style={{ padding: '24px 0' }}>Cargando abogados…</div>
                        ) : (
                            <select className="form-select" style={{ marginTop: 14 }} value={abogadoSel}
                                    onChange={e => setAbogadoSel(e.target.value)}>
                                <option value="">— elige un abogado —</option>
                                {abogados.map(a => (
                                    <option key={a._id} value={a._id}
                                            disabled={a._id === String(inc.assignedLawyer?._id || '')}>
                                        {a.name}
                                        {a.zonas ? ` — ${a.zonas} ${a.zonas === 1 ? 'zona' : 'zonas'}` : ' — sin zonas'}
                                        {a.esGuardia ? ' · guardia' : ''}
                                        {a._id === String(inc.assignedLawyer?._id || '') ? ' (actual)' : ''}
                                    </option>
                                ))}
                            </select>
                        )}
                        <p className="aviso">
                            Queda constancia en el expediente y el abogado recibe el mismo aviso que en una asignación automática.
                        </p>
                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setReasignando(false)}>Cancelar</button>
                            <button className="btn btn-primary btn-sm" onClick={confirmarReasignacion}
                                    disabled={!abogadoSel || guardandoAsignacion}>
                                {guardandoAsignacion ? 'Asignando…' : inc.assignedLawyer ? 'Reasignar' : 'Asignar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Sumar abogado al equipo del caso ───────────────────── */}
            {equipoAbierto && (
                <div className="ho-modal-bk" onClick={e => e.target === e.currentTarget && setEquipoAbierto(false)}>
                    <div className="ho-modal" role="dialog" aria-modal="true" aria-label="Sumar abogado al caso">
                        <div className="ho-kicker">Equipo del caso</div>
                        <h2 className="ho-h2" style={{ marginTop: 6 }}>Sumar un abogado</h2>
                        {abogados === null ? (
                            <div className="ho-empty" style={{ padding: '24px 0' }}>Cargando abogados…</div>
                        ) : (
                            <select className="form-select" style={{ marginTop: 14 }} value={coSel}
                                    onChange={e => setCoSel(e.target.value)}>
                                <option value="">— elige un abogado —</option>
                                {abogados.map(a => {
                                    const enCaso = a._id === String(inc.assignedLawyer?._id || '') ||
                                                   (inc.coLawyers || []).some(c => String(c._id) === a._id);
                                    return (
                                        <option key={a._id} value={a._id} disabled={enCaso}>
                                            {a.name}
                                            {a.zonas ? ` — ${a.zonas} ${a.zonas === 1 ? 'zona' : 'zonas'}` : ''}
                                            {enCaso ? ' (ya en el caso)' : ''}
                                        </option>
                                    );
                                })}
                            </select>
                        )}
                        <p className="aviso">
                            Verá el caso en su bandeja y podrá trabajarlo junto al titular. Queda constancia en el expediente.
                        </p>
                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setEquipoAbierto(false)}>Cancelar</button>
                            <button className="btn btn-primary btn-sm" onClick={sumarCoAbogado}
                                    disabled={!coSel || guardandoAsignacion}>
                                {guardandoAsignacion ? 'Sumando…' : 'Sumar al caso'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Validación del relato: fija la versión que se imprime ── */}
            {validando && (
                <div className="ho-modal-bk" onClick={e => e.target === e.currentTarget && setValidando(false)}>
                    <div className="ho-modal" role="dialog" aria-modal="true" aria-label="Validación del relato">
                        <div className="ho-kicker">Validación del relato</div>
                        <h2 className="ho-h2" style={{ marginTop: 6 }}>Versión oficial del expediente</h2>
                        <textarea value={textoOficial} onChange={e => setTextoOficial(e.target.value)} autoFocus />
                        <p className="aviso">
                            Esta versión se imprimirá en el documento oficial del folio <span className="ho-mono">{inc.folio}</span>.
                            El relato original se conserva sin cambios como anexo.
                        </p>
                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setValidando(false)}>Cancelar</button>
                            <button className="btn btn-primary btn-sm" onClick={validar} disabled={!textoOficial.trim()}>
                                Aprobar versión oficial
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Estaciones de la línea de tiempo: los eventos que marcan el avance del
// expediente se pintan como hitos (nodo sólido, título serif), distintos de la
// conversación y la documentación del día a día.
const TITULOS_HITO = new Set([
    'Folio emitido', 'Cambio de etapa', 'Abogado asignado', 'Abogado reasignado',
    'Documento emitido', 'Expediente cerrado',
]);

/** Una entrada del hilo. El tipo define color, ícono, kicker y forma. */
function Entrada({ e, onArchivo, attachments, onResolver, onSubirDoc }) {
    const k = KIND[e.kind] || KIND.sistema;
    const esLegal = e.visibility === 'legal';
    const resuelto = !!e.pending?.resolvedAt;
    const esHito = e.kind === 'hecho' || e.kind === 'validacion' ||
                   (e.kind === 'sistema' && TITULOS_HITO.has(e.title));
    // El cambio de etapa dice A QUÉ etapa llegó, no el genérico "Cambio de etapa".
    const titulo = (e.kind === 'sistema' && e.title === 'Cambio de etapa' && e.meta?.a)
        ? `Etapa: ${etapaLabel(e.meta.a)}`
        : e.title;
    // Pendiente de sistema: se resuelve SOLO cuando se asigna abogado (por
    // zona o con el botón del expediente). Nadie lo cierra a mano — es un
    // estatus, no una tarea. El backend también lo rechaza.
    const autoResoluble = e.title === 'Sin abogado asignado';

    const idxDe = (key) => (attachments || []).findIndex(a => a.key === key);

    return (
        <article className={`ho-ev ${esLegal ? 'legal' : ''} ${esHito ? 'hito' : ''}`}>
            <div className="t">{fmtHora(e.occurredAt || e.createdAt)}</div>
            <div className="sp"><span className="node" style={{ color: k.color }}>{I[k.ico]}</span></div>
            <div className="bd">
                <div className="ho-kicker" style={{ color: k.color }}>
                    {e.kind === 'mensaje' && e.author?.name ? e.author.name : k.label}
                    {esLegal && <span className="ho-privado">{I.lock} Solo abogados</span>}
                </div>

                {e.kind === 'pendiente' ? (
                    <div className={`ho-pend-box ${resuelto ? 'hecho' : ''}`} style={{ marginTop: 8 }}>
                        <span style={{ color: resuelto ? 'var(--ho-ok)' : 'var(--ho-pend)' }}>
                            {resuelto ? I.check : I.alert}
                        </span>
                        <div>
                            <div style={{ fontWeight: 600, textDecoration: resuelto ? 'line-through' : 'none' }}>{e.title}</div>
                            {e.body && <div style={{ fontSize: 12.5, color: 'var(--c-text-2)' }}>{e.body}</div>}
                        </div>
                        <span className="who">
                            {resuelto
                                ? `Resuelto ${fmtFecha(e.pending.resolvedAt)}`
                                : `${e.pending?.owner === 'legal' ? 'Equipo legal' : e.pending?.owner === 'rh' ? 'Recursos Humanos' : 'Gerencia de sucursal'} · ${diasDesde(e.createdAt)} d`}
                        </span>
                        {!resuelto && (autoResoluble ? (
                            <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>
                                Se resuelve al asignar abogado
                            </span>
                        ) : (
                            <span className="d-inline-flex gap-2">
                                {/* Si el pendiente ES un documento, la acción natural es
                                    subirlo aquí mismo: llega con su docType y el
                                    pendiente se cierra solo. "Marcar como resuelto"
                                    queda para cuando se entregó por otra vía. */}
                                {e.pending?.docKey && (
                                    <button className="ho-chip d-inline-flex align-items-center gap-1"
                                            onClick={() => onSubirDoc(e.pending.docKey)}>
                                        {I.clip} Subir documento
                                    </button>
                                )}
                                <button className="ho-chip" onClick={() => onResolver(e._id)}>Marcar como resuelto</button>
                            </span>
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Sin título duplicado: si repite lo que ya dice el
                            kicker del tipo ("El hecho"/"El hecho"), sobra. */}
                        {titulo && e.kind !== 'mensaje' && titulo !== k.label && <div className="ttl">{titulo}</div>}
                        {e.body && (e.kind === 'mensaje' || e.kind === 'bitacora'
                            ? <div className="ho-quote">{e.body}</div>
                            : <div className="dsc">{e.body}</div>)}
                    </>
                )}

                {e.files?.length > 0 && (
                    <div className="ho-files">
                        {e.files.map((f, i) => {
                            const idx = idxDe(f.key);
                            return (
                                <button key={i} className="ho-file" disabled={idx < 0}
                                        onClick={() => idx >= 0 && onArchivo(idx)}>
                                    {I.doc} {f.nombre || 'archivo'}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </article>
    );
}
